'use client'

import ProductDetailRelatedProducts from '@/app/(layout)/[serviceUrl]/_components/ProductDetailRelatedProducts'
import { trackViewItem } from '@/lib/analytics'
import { IProduct } from '@/types/interfaces/products'
import {
  ArrowLeft,
  CalendarDays,
  Check,
  Clock3,
  Info,
  MapPin,
  Play,
  ShoppingBag,
  Users,
  X,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import ProductDetailReservationForm from './ProductDetailReservationForm'
// TODO: 후기 기능 추가 시 활성화
// import ProductDetailReviews from './ProductDetailReviews'

const detailTabs = [
  ['intro', '프로그램 소개'],
  ['package', '포함/불포함'],
  ['notice', '주의사항'],
  ['refund', '취소 정책'],
  // TODO: 후기 기능 추가 시 활성화
  // ['reviews', '후기'],
] as const

const getYoutubeId = (value?: string | null) => {
  if (!value) return null

  try {
    const url = new URL(value)
    if (!['https:', 'http:'].includes(url.protocol)) return null
    const host = url.hostname.toLowerCase()
    const parts = url.pathname.split('/')
    const id =
      host === 'youtu.be'
        ? parts[1]
        : ['youtube.com', 'www.youtube.com', 'm.youtube.com'].includes(host)
          ? url.pathname === '/watch'
            ? url.searchParams.get('v')
            : ['shorts', 'embed', 'live'].includes(parts[1])
              ? parts[2]
              : null
          : null
    return id && /^[\w-]{11}$/.test(id) ? id : null
  } catch {
    return null
  }
}

interface IInfoCardProps {
  icon: React.ReactNode
  label: string
  value: string | null
}

const InfoCard = ({ icon, label, value }: IInfoCardProps) => (
  <div className="flex min-w-0 items-start gap-3 rounded-xl bg-gray-50 px-4 py-3">
    <span className="mt-0.5 text-emerald-400">{icon}</span>
    <div className="min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-gray-800">{value || '-'}</p>
    </div>
  </div>
)

interface ITextSectionProps {
  id: string
  title: string
  children: React.ReactNode
}

const TextSection = ({ id, title, children }: ITextSectionProps) => (
  <section id={id} className="scroll-mt-[calc(var(--detail-top,0px)+80px)] py-9">
    <h2 className="mb-6 border-b border-gray-100 pb-5 text-xl font-bold text-gray-900">{title}</h2>
    {children}
  </section>
)

interface IProductDetailPageProps {
  product: IProduct
  listPath: string
}

export default function ProductDetailPage({ product, listPath }: IProductDetailPageProps) {
  const [activeTab, setActiveTab] = useState<string>('intro')
  const pageRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const updatePosition = () => {
      const header = document.querySelector('header')
      const position = header && getComputedStyle(header).position
      const top =
        header && (position === 'fixed' || position === 'sticky')
          ? Math.max(0, header.getBoundingClientRect().bottom)
          : 0
      pageRef.current?.style.setProperty('--detail-top', `${top}px`)
      let current: string = 'intro'
      for (const [id] of detailTabs) {
        const section = pageRef.current?.querySelector(`#${id}`)
        if (section && section.getBoundingClientRect().top <= top + 100) current = id
      }
      setActiveTab(current)
    }
    updatePosition()
    window.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [])

  /** 구글 태그매니저 상품 상세 조회 이벤트 추적 */
  useEffect(() => {
    trackViewItem({ product: product })
  }, [product])

  const videoId = getYoutubeId(product.videoUrl)

  return (
    <main ref={pageRef} className="bg-white pb-28 text-gray-900 lg:pb-20">
      <div className="container pt-6">
        <div className="relative aspect-square w-full overflow-hidden rounded-3xl bg-gray-100 sm:aspect-[2.15/1]">
          <Image
            src={product.thumbnail}
            alt={product.name}
            fill
            priority
            unoptimized
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/10" />

          <Link
            href={listPath}
            className="absolute left-5 top-5 flex items-center gap-1 rounded-full bg-white/90 px-4 py-2 text-[14px] font-semibold shadow-sm"
          >
            <ArrowLeft size={16} />
            목록으로
          </Link>

          <div className="absolute inset-x-6 bottom-6 text-white">
            {!!product.keywords.length && (
              <span className="mb-3 inline-block rounded-full bg-emerald-400 px-3 py-[6px] text-[13px] font-bold">
                {product.category}
              </span>
            )}
            <h1 className="text-3xl font-bold sm:text-4xl">{product.name}</h1>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <InfoCard
            icon={<CalendarDays size={18} />}
            label="운영 기간"
            value={product.scheduleValue}
          />
          <InfoCard icon={<Clock3 size={18} />} label="소요 시간" value={product.timeRequired} />
          <InfoCard icon={<Users size={18} />} label="운영 시간" value={product.startTime} />
          <InfoCard icon={<MapPin size={18} />} label="장소" value={product.place} />
        </div>

        <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0">
            <nav
              aria-label="상품 상세 메뉴"
              className="scrollbar-thin sticky top-[var(--detail-top,0px)] z-20 flex overflow-x-auto border-b border-gray-200 bg-white text-sm"
            >
              {detailTabs.map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  aria-current={activeTab === id ? 'location' : undefined}
                  onClick={() => setActiveTab(id)}
                  className={`flex-1 whitespace-nowrap border-b-2 px-4 py-5 text-center font-semibold transition-colors hover:text-gray-900 ${activeTab === id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400'}`}
                >
                  {label}
                </a>
              ))}
            </nav>

            <TextSection id="intro" title="프로그램 소개">
              <div className="prose max-w-none break-keep text-sm leading-7 text-gray-600">
                {product.description && <p>{product.description}</p>}
              </div>
              {product.contentHtml ? (
                <div
                  className="prose max-w-none text-sm leading-7 text-gray-600"
                  dangerouslySetInnerHTML={{ __html: product.contentHtml }}
                />
              ) : (
                <p className="whitespace-pre-line text-sm leading-7 text-gray-600">
                  {product.content || '등록된 소개가 없습니다.'}
                </p>
              )}
              {product.place && (
                <div className="mt-6 flex items-center gap-3 rounded-xl bg-gray-50 px-5 py-4">
                  <MapPin size={20} className="shrink-0 text-emerald-400" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-400">장소</p>
                    <p className="mt-1 break-words text-sm font-semibold">{product.place}</p>
                  </div>
                  <a
                    href={
                      product.latitude != null && product.longitude != null
                        ? `https://map.kakao.com/link/map/${product.latitude},${product.longitude}`
                        : `https://map.kakao.com/link/search/${encodeURIComponent(product.place.split('\n')[0].trim())}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-md transition-shadow hover:shadow-lg"
                  >
                    지도보기
                  </a>
                </div>
              )}
              {!!product.checkValues.length && (
                <div className="mt-8">
                  <h3 className="mb-4 text-sm font-bold">포함 활동</h3>
                  <ul className="space-y-3 text-sm text-gray-600">
                    {product.checkValues.map((activity, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check
                          size={20}
                          className="shrink-0 rounded-full bg-emerald-100 p-1 text-emerald-600"
                        />
                        {activity}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </TextSection>

            <TextSection id="package" title="포함/불포함">
              <div className="space-y-8">
                {[
                  ['포함 사항', product.infoIncludeContent],
                  ['불포함 사항', product.infoNotIncludeContent],
                  ['참고 사항', product.tipValue],
                ].map(([title, content]) => (
                  <div key={title}>
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-bold">
                      {title === '포함 사항' ? (
                        <Check size={22} className="rounded-full bg-emerald-500 p-1 text-white" />
                      ) : title === '불포함 사항' ? (
                        <X size={22} className="rounded-full bg-gray-200 p-1 text-gray-500" />
                      ) : (
                        <ShoppingBag
                          size={22}
                          className="rounded-full bg-blue-100 p-1 text-blue-500"
                        />
                      )}
                      {title}
                    </h3>
                    <ul
                      className={`ml-4 list-disc space-y-2 text-sm leading-6 text-gray-600 ${title === '포함 사항' ? 'marker:text-emerald-500' : title === '불포함 사항' ? 'marker:text-gray-300' : 'marker:text-blue-300'}`}
                    >
                      {(content || '등록된 내용이 없습니다.')
                        .split('\n')
                        .filter(line => line.trim())
                        .map((line, index) => (
                          <li key={index}>{line}</li>
                        ))}
                    </ul>
                  </div>
                ))}
              </div>
            </TextSection>

            <TextSection id="notice" title="주의사항">
              <div className="flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50/60 p-5">
                <Info size={18} className="mt-1 shrink-0 text-amber-500" />
                <p className="whitespace-pre-line text-sm leading-7 text-gray-600">
                  {product.infoContent || '등록된 주의사항이 없습니다.'}
                </p>
              </div>
            </TextSection>

            <TextSection id="refund" title="취소 정책">
              <div className="overflow-hidden rounded-2xl border border-gray-200">
                <div className="bg-gray-900 px-6 py-4 text-sm font-semibold text-white">
                  취소 및 환불 안내
                </div>
                <p className="whitespace-pre-line bg-gray-50 px-6 py-5 text-sm leading-7 text-gray-600">
                  {product.refundContent || '등록된 취소 정책이 없습니다.'}
                </p>
              </div>
            </TextSection>
            <TextSection id="gallery" title="갤러리">
              {videoId || product.representativeImageUrls.length ? (
                <div className="grid grid-cols-2 gap-4">
                  {videoId && (
                    <a
                      href={`https://www.youtube.com/watch?v=${videoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${product.name} 영상 유튜브에서 보기 (새 창)`}
                      className="group relative col-span-2 aspect-video overflow-hidden rounded-2xl bg-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-500"
                    >
                      <Image
                        src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
                        alt={`${product.name} 영상 썸네일`}
                        fill
                        unoptimized
                        className="object-cover transition-transform group-hover:scale-105"
                      />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25">
                        <span className="flex size-14 items-center justify-center rounded-full bg-white/95 text-gray-900 shadow-lg">
                          <Play size={24} fill="currentColor" aria-hidden="true" className="ml-1" />
                        </span>
                      </span>
                      <span className="absolute bottom-3 left-3 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white">
                        YouTube
                      </span>
                    </a>
                  )}
                  {product.representativeImageUrls.map((src, index) => (
                    <a
                      key={`${src}-${index}`}
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      className="relative aspect-[1.6] overflow-hidden rounded-2xl bg-gray-100"
                    >
                      <Image
                        src={src}
                        alt={`${product.name} 사진 ${index + 1} (새 창)`}
                        fill
                        unoptimized
                        className="object-cover transition-transform hover:scale-105"
                      />
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">등록된 사진이 없습니다.</p>
              )}
            </TextSection>
            {/* TODO: 후기 기능 추가 시 활성화 */}
            {/* <ProductDetailReviews /> */}
            <ProductDetailRelatedProducts mallLinks={product.mallLinks ?? []} />
          </div>

          <aside className="lg:pt-4">
            <ProductDetailReservationForm product={product} termsPath={listPath} />
          </aside>
        </div>
      </div>
    </main>
  )
}
